import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, Center, Flex, Image, Spinner, Text,
} from '@chakra-ui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LuCheck, LuChevronDown } from 'react-icons/lu';
import { useAccountsOverview } from '../../hooks/useAccountsOverview';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { useDisplayCurrency } from '../../contexts/DisplayCurrencyContext';
import { useUsdRates } from '../../hooks/useUsdRates';
import { fetchAccountFreshness } from '../../api/endpointsBackend';
import { assetsMinusDebt, assetsMinusDebtByCurrency } from '../../utils/assetsMinusDebt';
import { CATEGORY_ORDER, isDebtAccount } from '../../utils/accountTypes';
import { formatPhoneBalance } from '../../utils/formatters';
import { formatDayShort, monthShort } from '../../utils/intlFormatters';
import { todayLocalISO } from '../../utils/localDate';
import TimeSeriesChart, { chartColor } from '../shared/TimeSeriesChart';
import UnconvertibleNote from '../shared/UnconvertibleNote';
import { usePhoneShell, usePhoneTopBar } from './PhoneShellContext';
import PhoneSheet from './PhoneSheet';
import { Chip } from './PhoneMovements';
import DesktopOnlyNote from './DesktopOnlyNote';
import { DesktopNoteCard } from './PhoneReadOnlyParts';
import { usePhoneAccountSheet } from './PhoneAccountSheet';
import { gapFigures, gapMonth } from './PhoneAccountSections';
import { nestAccounts, isSettledLoan } from '../../utils/subAccounts';
import { subAccountView, holdingsLine } from '../accounts/subAccountView';
import {
  EMPTY_GROUP_LABEL, GROUP_BY_OPTIONS, GROUP_BY_TYPE, groupAccounts, readGroupBy, saveGroupBy, signedForTotal,
} from '../../utils/accountGrouping';

// The phone keeps three of the desktop's six ranges.
const RANGES = ['1M', '3M', '1Y'];
// Statement statuses that mean the account's statements are not current.
const LATE_STATUSES = ['behind', 'outdated'];
// At 320px (Display Zoom) the headline figure steps down so it stays on one line.
const NARROW = '@media screen and (max-width: 359.98px)';
// The desktop's create form, which a deep link can name.
const CREATE_MODE = 'new';
// Where a row's text column starts (16px gutter, 36px logo, 12px gap): the
// hairlines between rows start there.
const ROW_TEXT_X = '64px';
// The same for a sub-account row, indented under its parent.
const NESTED_TEXT_X = '88px';
// A currency code keeps its own case inside an uppercase caption.
const AS_WRITTEN = { textTransform: 'none' };

const orderedGroups = (byGroup) => {
  const present = Object.keys(byGroup);
  return [
    ...CATEGORY_ORDER.filter((g) => present.includes(g)),
    ...present.filter((g) => !CATEGORY_ORDER.includes(g)).sort(),
  ];
};

/**
 * "COP 2,797,200", or one figure per currency joined. `approx` marks a figure
 * that holds a converted balance with ≈.
 */
function amountText({ approx, currency, value, perCurrency }) {
  if (perCurrency) return perCurrency.map((l) => formatPhoneBalance(l.currency, l.value)).join(' · ');
  return `${approx ? '≈ ' : ''}${formatPhoneBalance(currency, value)}`;
}

/** The pill on the top bar: which currency the figures are shown in. */
function CurrencyPill({ label, onClick }) {
  const { t } = useTranslation('common');
  return (
    <Button
      variant="ghost"
      h="44px"
      px="8px"
      onClick={onClick}
      aria-label={`${t('convert.convertTo')}: ${label}`}
    >
      {/* Capped so it never reaches the centred title at 320px. */}
      <Flex
        align="center"
        gap="4px"
        h="32px"
        maxW="104px"
        px="12px"
        borderRadius="full"
        border="1px solid"
        borderColor="redesign.border"
        bg="redesign.bg.card"
        color="redesign.heading"
        fontSize="15px"
        fontWeight="600"
      >
        <Box as="span" noOfLines={1} minW={0}>{label}</Box>
        <Box as={LuChevronDown} boxSize="16px" color="phone.muted" flexShrink={0} />
      </Flex>
    </Button>
  );
}

/**
 * "Show amounts in": per currency, or everything converted into one. A
 * currency with no rate is left out: converting into it cannot work.
 */
function CurrencySheet({ isOpen, onClose }) {
  const { t } = useTranslation('accounts');
  const { displayCurrency, setDisplayCurrency, activeCurrencies } = useDisplayCurrency();
  const { rates } = useUsdRates();
  const convertible = rates ? activeCurrencies.filter((c) => rates[c] != null) : activeCurrencies;
  // The unconverted option carries the pill's own label, so the pill always
  // reads as the option it names.
  const options = [
    { value: null, label: t('phone.pillAll'), hint: t('phone.pillAllHint') },
    ...convertible.map((c) => ({ value: c, label: t('phone.convertedIn', { currency: c }) })),
  ];
  return (
    <PhoneSheet id="accounts-currency" isOpen={isOpen} onClose={onClose} title={t('phone.showAmountsIn')}>
      {options.map((o) => {
        const active = (displayCurrency || null) === o.value;
        return (
          <Flex
            key={o.value || 'all'}
            as="button"
            type="button"
            align="center"
            justify="space-between"
            w="100%"
            minH="52px"
            px="16px"
            py="6px"
            gap="12px"
            textAlign="left"
            onClick={() => { setDisplayCurrency(o.value); onClose(); }}
            _active={{ bg: 'redesign.bg.hover' }}
          >
            <Box minW={0}>
              <Text textStyle="phoneBody" color="redesign.heading">{o.label}</Text>
              {o.hint && <Text textStyle="phoneMeta" color="phone.muted">{o.hint}</Text>}
            </Box>
            {active && <Box as={LuCheck} boxSize="20px" color="phone.link" />}
          </Flex>
        );
      })}
    </PhoneSheet>
  );
}

/** "Group by": the list by type of account, by institution or by age, as on the desktop's Display menu. */
function GroupBySheet({ isOpen, onClose, value, onChange }) {
  const { t } = useTranslation('accounts');
  return (
    <PhoneSheet id="accounts-group-by" isOpen={isOpen} onClose={onClose} title={t('groupBy.label')}>
      {GROUP_BY_OPTIONS.map((option) => (
        <Flex
          key={option}
          as="button"
          type="button"
          align="center"
          justify="space-between"
          w="100%"
          minH="52px"
          px="16px"
          py="6px"
          gap="12px"
          textAlign="left"
          onClick={() => { onChange(option); onClose(); }}
          _active={{ bg: 'redesign.bg.hover' }}
        >
          <Text textStyle="phoneBody" color="redesign.heading">{t(`groupBy.${option}`)}</Text>
          {value === option && <Box as={LuCheck} boxSize="20px" color="phone.link" />}
        </Flex>
      ))}
    </PhoneSheet>
  );
}

/**
 * The labels under the chart, each at its point's position (0 to 1). 1M
 * names three days; 3M and 1Y name the months, at the point each one starts,
 * thinned to at most five.
 */
function axisLabels(periods, range) {
  const n = periods.length;
  if (n < 2) return [];
  const at = (i) => i / (n - 1);
  if (range === '1M') {
    return [0, Math.floor((n - 1) / 2), n - 1].map((i) => ({ x: at(i), label: formatDayShort(periods[i]), tick: false }));
  }
  const starts = [];
  periods.forEach((p, i) => {
    const month = p.slice(5, 7);
    const first = i === 0 ? p.slice(8, 10) === '01' : month !== periods[i - 1].slice(5, 7);
    if (first) starts.push({ x: at(i), label: monthShort(Number(month) - 1), tick: i > 0 });
  });
  const step = Math.ceil(starts.length / 5);
  return starts.filter((_, i) => (starts.length - 1 - i) % step === 0);
}

/** A label centred on its point, kept inside the card at either end. */
const labelShift = (x) => {
  if (x < 0.06) return 'translateX(0)';
  if (x > 0.94) return 'translateX(-100%)';
  return 'translateX(-50%)';
};

function HeaderCard({
  figures, perCurrency, approx, currency, unconvertible, net, periods, granularity, range, onRange,
}) {
  const { t } = useTranslation('accounts');
  const labels = axisLabels(periods, range);
  const value = (text) => <Box as="span" color="redesign.text">{text}</Box>;
  return (
    <Box
      bg="redesign.bg.surface"
      border="1px solid"
      borderColor="redesign.border"
      borderRadius="16px"
      p="16px"
    >
      <Text textStyle="phoneMeta" color="phone.muted">{t('phone.assetsMinusDebt')}</Text>
      {perCurrency ? (
        perCurrency.map((l, i) => (
          <Box key={l.currency} mt={i ? '8px' : '2px'}>
            <Text fontSize={i ? '20px' : '28px'} lineHeight={i ? '26px' : '34px'} fontWeight="700" color="redesign.heading"
              sx={{ fontVariantNumeric: 'tabular-nums', ...(i ? {} : { [NARROW]: { fontSize: '25px', lineHeight: '30px' } }) }}>
              {formatPhoneBalance(l.currency, l.net)}
            </Text>
            <Flex wrap="wrap" columnGap="16px" textStyle="phoneMeta" color="phone.muted">
              <Box as="span">{t('overview.assets')} {value(formatPhoneBalance(l.currency, l.assets))}</Box>
              {/* A currency nobody owes in has no debt line. */}
              {l.debt !== 0 && <Box as="span">{t('overview.debt')} {value(formatPhoneBalance(l.currency, l.debt))}</Box>}
            </Flex>
          </Box>
        ))
      ) : (
        <>
          <Text
            textStyle="phoneHero"
            fontSize="28px"
            lineHeight="34px"
            mt="2px"
            color="redesign.heading"
            whiteSpace="nowrap"
            sx={{ [NARROW]: { fontSize: '25px', lineHeight: '30px' } }}
          >
            {amountText({ approx: approx.net, currency, value: figures.net })}
          </Text>
          <Flex wrap="wrap" columnGap="16px" rowGap="2px" mt="4px" textStyle="phoneMeta" color="phone.muted">
            <Box as="span">{t('overview.assets')} {value(amountText({ approx: approx.assets, currency, value: figures.assets }))}</Box>
            <Box as="span">{t('overview.debt')} {value(amountText({ approx: approx.debt, currency, value: figures.debt }))}</Box>
          </Flex>
          {/* Balances in a currency with no rate are left out of the ≈
              figures, and say so. */}
          {unconvertible?.num_unconvertible > 0 && (
            <Box mt="6px" sx={{ '& button': { minWidth: '44px', minHeight: '44px', m: '-12px', justifyContent: 'center' } }}>
              <UnconvertibleNote
                buckets={unconvertible.unconvertible}
                count={unconvertible.num_unconvertible}
                variant="balances"
                date={todayLocalISO()}
                tone="red"
                fontSize="13px"
              />
            </Box>
          )}
        </>
      )}

      {/* The end dot sits a few pixels in from the edge, so it is not cut. */}
      <Box mt="12px" pr="6px" sx={{ '& svg': { overflow: 'visible' } }}>
        <TimeSeriesChart
          series={[
            { key: 'net', values: net, color: chartColor('redesign.accent.assets'), strokeWidth: 2.5, fill: true },
          ]}
          periods={periods}
          granularity={granularity}
          height={88}
          interactive={false}
          showHoverLabel={false}
          my={0}
        />
        <Box position="relative" h="20px" borderTop="1px solid" borderColor="redesign.border" aria-hidden="true">
          {labels.map((l) => (
            <React.Fragment key={`${l.label}-${l.x}`}>
              {l.tick && <Box position="absolute" top="-4px" left={`${l.x * 100}%`} w="1px" h="4px" bg="redesign.text.faint" />}
              <Box
                as="span"
                position="absolute"
                top="4px"
                left={`${l.x * 100}%`}
                transform={labelShift(l.x)}
                fontSize="11px"
                lineHeight="14px"
                color="phone.muted"
                whiteSpace="nowrap"
              >
                {l.label}
              </Box>
            </React.Fragment>
          ))}
        </Box>
      </Box>
      {/* The chosen range sits raised on a sunken track. */}
      <Flex
        mt="8px"
        p="2px"
        gap="2px"
        borderRadius="12px"
        bg="redesign.bg.sunken"
        border="1px solid"
        borderColor="redesign.border"
        role="group"
      >
        {RANGES.map((r) => {
          const active = range === r;
          return (
            <Box
              key={r}
              as="button"
              type="button"
              flex="1"
              h="44px"
              borderRadius="10px"
              fontSize="14px"
              fontWeight={active ? '600' : '500'}
              bg={active ? 'redesign.bg.raised' : 'transparent'}
              boxShadow={active ? 'sm' : 'none'}
              color={active ? 'redesign.heading' : 'phone.muted'}
              aria-pressed={active}
              onClick={() => onRange(r)}
            >
              {r}
            </Box>
          );
        })}
      </Flex>
    </Box>
  );
}

function AccountLogo({ account }) {
  return (
    <Flex
      w="36px"
      h="36px"
      borderRadius="full"
      bg="redesign.bg.surface"
      border="1px solid"
      borderColor="redesign.border"
      align="center"
      justify="center"
      flexShrink={0}
      fontSize="18px"
      overflow="hidden"
    >
      {account.source_logo_url ? (
        <Image
          src={account.source_logo_url}
          alt=""
          boxSize="22px"
          objectFit="contain"
          fallback={<Text as="span" fontSize="18px">{account.icon || '🏦'}</Text>}
        />
      ) : (account.icon || '🏦')}
    </Flex>
  );
}

/**
 * Line 2 of a row: what the account needs, in words, the most pressing
 * first. Movements to review, then a statement that does not tie out, then a
 * statement that is behind, then a match; otherwise the institution
 * (`fallback`, which a foreign-currency row gives up for its converted
 * figure). A closed account says only that.
 */
function statusLine(account, freshness, t) {
  if (account.is_active === false) return { text: t('phone.closed'), color: 'phone.muted' };
  if (account.needs_review_count > 0) {
    return { text: t('row.toReview', { count: account.needs_review_count }), color: 'phone.alert' };
  }
  const rec = account.reconciliation;
  if (rec?.status === 'off' && rec.gaps?.length) {
    const gap = rec.gaps[0];
    return {
      text: t('phone.offBy', { amount: formatPhoneBalance(gap.currency, gapFigures(gap).diff), month: gapMonth(gap) }),
      color: 'phone.expense',
    };
  }
  if (freshness && LATE_STATUSES.includes(freshness.status) && freshness.covered_through) {
    return { text: t('phone.behind', { date: formatDayShort(freshness.covered_through) }), color: 'phone.muted' };
  }
  if (rec?.status === 'matches') return { text: t('phone.matches'), color: 'phone.income' };
  // The note is free text, not an institution, so it is not shown here.
  return { text: account.source || t('row.manualAccount'), color: 'phone.muted', fallback: true };
}

/**
 * The phone's account row: the name and balance get the full width (no
 * sparkline or delta pill), and line 2 says what the account needs. A
 * foreign-currency account with nothing else to say shows its converted
 * figure there instead of the institution. A closed account is drawn in the
 * muted colour, which keeps 4.5:1, rather than faded.
 */
function PhoneAccountRow({ account, lines: balanceLines, converted, freshness, divider, onClick, nested = false }) {
  const { t } = useTranslation('accounts');
  // A holding, a loan or a person with loans shows its own figures and says
  // what they are on line 2, unless something more pressing needs saying.
  const subView = subAccountView(account, t);
  const lines = subView ? subView.lines.map((l) => ({ currency: l.currency, balance: l.value, pct: l.pct, pctSuffix: l.pctSuffix })) : balanceLines;
  let found = statusLine(account, freshness, t);
  if (subView && found.fallback) found = { text: subView.subtitle, color: 'phone.muted' };
  const holdings = holdingsLine(account, t);
  const convertedAsLine2 = !subView && Boolean(converted) && lines.length === 1 && found.fallback;
  const status = convertedAsLine2 ? { text: converted, color: 'phone.muted' } : found;
  const convertedUnder = !subView && converted && !convertedAsLine2 ? converted : null;
  const stacked = lines.length > 1 || Boolean(convertedUnder) || Boolean(holdings) || lines.some((l) => l.pct != null);
  const closed = account.is_active === false;
  const ink = closed ? 'phone.muted' : 'redesign.heading';
  const statusText = <Text textStyle="phoneMeta" color={status.color}>{status.text}</Text>;
  return (
    <Box
      as="button"
      type="button"
      onClick={() => onClick(account)}
      position="relative"
      display="flex"
      gap="12px"
      alignItems="flex-start"
      w="100%"
      minH="64px"
      px="16px"
      pl={nested ? '40px' : '16px'}
      py="10px"
      textAlign="left"
      _active={{ bg: 'redesign.bg.hover' }}
      _before={divider ? {
        content: '""', position: 'absolute', top: 0, left: nested ? NESTED_TEXT_X : ROW_TEXT_X, right: 0, h: '1px', bg: 'redesign.border',
      } : undefined}
    >
      <Box pt="2px"><AccountLogo account={account} /></Box>
      {/* The name keeps a readable width; amounts that cannot sit beside it
          wrap under it, right-aligned, instead of squeezing it. One balance:
          line 2 runs the full width, under the amount too. Several: the
          amounts stack on the right and line 2 stays under the name. */}
      <Flex flex="1" minW={0} wrap="wrap" columnGap="12px" rowGap="2px" align="flex-start">
        <Box flex="1 1 120px" minW="120px">
          <Text textStyle="phoneRowName" color={ink}>{account.name}</Text>
          {stacked && statusText}
        </Box>
        <Box ml="auto" textAlign="right" flexShrink={0}>
          {lines.map((l) => (
            <React.Fragment key={l.currency}>
              <Text textStyle="phoneRowAmount" color={ink} whiteSpace="nowrap">
                {formatPhoneBalance(l.currency, l.balance)}
              </Text>
              {l.pct != null && (
                <Text textStyle="phoneMeta" whiteSpace="nowrap" color={l.pct > 0 ? 'phone.income' : l.pct < 0 ? 'phone.expense' : 'phone.muted'}>
                  {`${l.pct > 0 ? '+' : l.pct < 0 ? '−' : ''}${Math.abs(l.pct).toFixed(2)}%${l.pctSuffix || ''}`}
                </Text>
              )}
            </React.Fragment>
          ))}
          {convertedUnder && (
            <Text textStyle="phoneMeta" color="phone.muted" whiteSpace="nowrap">{convertedUnder}</Text>
          )}
          {holdings && (
            <Text textStyle="phoneMeta" color="phone.muted" whiteSpace="nowrap">{holdings}</Text>
          )}
        </Box>
        {!stacked && <Box flexBasis="100%">{statusText}</Box>}
      </Flex>
    </Box>
  );
}

/** A 44px link line under a parent: show or hide its holdings or loans, or its settled loans. */
function ListLink({ indent = false, onClick, children }) {
  return (
    <Box
      as="button"
      type="button"
      w="100%"
      h="44px"
      px="16px"
      pl={indent ? '40px' : '16px'}
      textAlign="left"
      color="phone.link"
      fontSize="15px"
      onClick={onClick}
    >
      {children}
    </Box>
  );
}

// Add or remove an id from a Set held in state.
const toggleIn = (setter, id) => setter((prev) => {
  const next = new Set(prev);
  if (next.has(id)) next.delete(id); else next.add(id);
  return next;
});

function AccountGroup({ group, label, accounts, caption, rowProps, onOpen }) {
  const { t } = useTranslation('accounts');
  const [showClosed, setShowClosed] = useState(false);
  // Parents whose sub-accounts are folded, and people whose settled loans show.
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [showSettled, setShowSettled] = useState(() => new Set());
  const closed = accounts.filter((a) => a.is_active === false);
  const visible = showClosed ? accounts : accounts.filter((a) => a.is_active !== false);

  return (
    <Box mt="20px">
      <Text textStyle="phoneCaption" color="phone.muted" px="16px" pb="4px">
        {label ?? t(`categories.${group}`, { defaultValue: group })}
        {caption && <Box as="span" sx={AS_WRITTEN}>{` · ${caption}`}</Box>}
      </Text>
      {/* Holdings and loans sit indented under their parent, which folds them. */}
      {nestAccounts(visible).map(({ account: a, children }, i) => {
        const expanded = !collapsed.has(a.id);
        const settled = children.filter(isSettledLoan);
        const open = children.filter((c) => !isSettledLoan(c));
        return (
          <React.Fragment key={a.id}>
            <PhoneAccountRow account={a} {...rowProps(a)} divider={i > 0} onClick={onOpen} />
            {expanded && open.map((c) => (
              <PhoneAccountRow key={c.id} account={c} {...rowProps(c)} divider nested onClick={onOpen} />
            ))}
            {expanded && settled.length > 0 && (
              <ListLink indent onClick={() => toggleIn(setShowSettled, a.id)}>
                {t(showSettled.has(a.id) ? 'phone.hideSettled' : 'phone.showSettled', { count: settled.length })}
              </ListLink>
            )}
            {expanded && showSettled.has(a.id) && settled.map((c) => (
              <PhoneAccountRow key={c.id} account={c} {...rowProps(c)} divider nested onClick={onOpen} />
            ))}
            {children.length > 0 && (
              <ListLink indent onClick={() => toggleIn(setCollapsed, a.id)}>
                {t(`phone.${expanded ? 'hide' : 'show'}${a.account_type === 'lend' ? 'Loans' : 'Holdings'}`, { count: children.length })}
              </ListLink>
            )}
          </React.Fragment>
        );
      })}
      {closed.length > 0 && (
        <Box
          as="button"
          type="button"
          w="100%"
          h="44px"
          px="16px"
          textAlign="left"
          color="phone.link"
          fontSize="15px"
          onClick={() => setShowClosed((v) => !v)}
        >
          {t(showClosed ? 'phone.hideClosed' : 'phone.showClosed', { count: closed.length })}
        </Box>
      )}
    </Box>
  );
}

/**
 * Accounts on the phone, read-only: what the accounts add up to, each
 * balance with one line on whether it ties to the bank, and a sheet per
 * account. Reads the same overview data the desktop page loads.
 */
export default function PhoneAccounts() {
  const { t } = useTranslation('accounts');
  const { can } = useWorkspace();
  const canReadFiles = can('source_file.read');
  const { displayCurrency, activeCurrencies } = useDisplayCurrency();
  const {
    loading, error, refetch,
    range, setRange,
    currency, availableCurrencies,
    accounts, accountsByGroup,
    accountStats, perCurrencyAccountStats, convertedAccountStats, convertedSummary,
    roiByAccount, chartPeriods, granularity, balanceBuckets,
  // The phone opens the chart on three months.
  } = useAccountsOverview({ initialRange: '3M' });
  const { refreshSignal } = usePhoneShell();
  const { openAccount, accountSheet } = usePhoneAccountSheet();
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const [groupByOpen, setGroupByOpen] = useState(false);
  const [freshness, setFreshness] = useState(new Map());
  const [groupBy, setGroupBy] = useState(readGroupBy);
  const byType = groupBy === GROUP_BY_TYPE;
  const groups = useMemo(
    () => groupAccounts(groupBy, accounts || [], accountsByGroup),
    [groupBy, accounts, accountsByGroup],
  );
  const location = useLocation();
  const navigate = useNavigate();
  const deepLink = new URLSearchParams(location.search).get('account_id');

  // A fix or edit anywhere on the phone reloads the balances and the review
  // counts in place; the first render has just loaded them.
  const firstSignal = useRef(true);
  useEffect(() => {
    if (firstSignal.current) {
      firstSignal.current = false;
      return;
    }
    refetch();
  }, [refreshSignal, refetch]);

  // "Statement behind" comes from the same freshness the Statements page shows.
  useEffect(() => {
    if (!canReadFiles) return undefined;
    let active = true;
    fetchAccountFreshness()
      .then((data) => {
        if (active) setFreshness(new Map((data?.accounts || []).map((a) => [a.account_id, a])));
      })
      .catch(() => { /* line 2 falls back to the next thing it can say */ });
    return () => { active = false; };
  }, [canReadFiles]);

  const converted = Boolean(convertedAccountStats);
  // Several currencies and no conversion: every figure is kept per currency.
  const perCurrencyView = !converted && availableCurrencies.length > 1;
  const figureCurrency = converted ? displayCurrency : currency;
  const stats = convertedAccountStats || accountStats;

  usePhoneTopBar({
    title: t('title'),
    right: activeCurrencies.length > 1 ? (
      <CurrencyPill label={displayCurrency || t('phone.pillAll')} onClick={() => setCurrencyOpen(true)} />
    ) : undefined,
  }, [t, displayCurrency, activeCurrencies.length]);

  const figures = useMemo(() => assetsMinusDebt(accounts, stats), [accounts, stats]);
  // The chart's currency leads, so the line ends on the first figure.
  const perCurrencyFigures = useMemo(() => {
    if (!perCurrencyView) return null;
    const list = assetsMinusDebtByCurrency(accounts, perCurrencyAccountStats);
    return [...list.filter((l) => l.currency === currency), ...list.filter((l) => l.currency !== currency)];
  }, [perCurrencyView, accounts, perCurrencyAccountStats, currency]);

  // Assets minus debt over the range, from the same open accounts as the
  // figure, so the line ends where the figure stands.
  const net = useMemo(() => {
    const out = new Array(chartPeriods.length).fill(0);
    accounts.forEach((a) => {
      if (a.is_active === false) return;
      const sign = isDebtAccount(a.account_group, a.account_type) ? -1 : 1;
      (stats.get(a.id)?.sparkline || []).forEach((v, i) => {
        // A debt series is already a positive amount owed.
        if (i < out.length) out[i] += sign * (v || 0);
      });
    });
    return out;
  }, [accounts, stats, chartPeriods.length]);

  // A converted figure is marked ≈ only when one of its open accounts holds
  // another currency; a COP-only total stays exact.
  const holdsForeign = (list) => converted && list.some((a) => a.is_active !== false
    && (perCurrencyAccountStats.get(a.id) || []).some((l) => l.currency !== displayCurrency));
  const approx = {
    net: holdsForeign(accounts),
    assets: holdsForeign(accounts.filter((a) => !isDebtAccount(a.account_group, a.account_type))),
    debt: holdsForeign(accounts.filter((a) => isDebtAccount(a.account_group, a.account_type))),
  };

  // Grouped other than by type, a group mixes kinds, so its debt counts against it.
  const forTotal = (a, value) => (byType ? value : signedForTotal(a, value));
  const groupCaption = (list) => {
    const open = list.filter((a) => a.is_active !== false);
    if (perCurrencyView) {
      const byCur = new Map();
      open.forEach((a) => (perCurrencyAccountStats.get(a.id) || []).forEach((l) => {
        byCur.set(l.currency, (byCur.get(l.currency) || 0) + forTotal(a, l.balance));
      }));
      if (!byCur.size) return '';
      return amountText({ perCurrency: [...byCur].map(([c, value]) => ({ currency: c, value })) });
    }
    const total = open.reduce((s, a) => s + forTotal(a, stats.get(a.id)?.balance || 0), 0);
    return amountText({ approx: holdsForeign(list), currency: figureCurrency, value: total });
  };

  const rowProps = (a) => {
    const native = perCurrencyAccountStats.get(a.id) || [];
    const lines = native.length
      ? native
      : [{ currency: figureCurrency, balance: stats.get(a.id)?.balance || 0 }];
    const foreign = converted && native.some((l) => l.currency !== displayCurrency);
    return {
      lines,
      converted: foreign ? `≈ ${formatPhoneBalance(displayCurrency, convertedAccountStats.get(a.id)?.balance || 0)}` : null,
      freshness: freshness.get(a.id),
    };
  };

  const openSheet = (a) => openAccount(a.id, {
    reconciliation: a.reconciliation,
    buckets: balanceBuckets,
    currency,
    roi: roiByAccount?.get(a.id),
  });
  const openSheetRef = useRef(openSheet);
  openSheetRef.current = openSheet;

  // `?account_id=<id>` (the desktop's link to an account) opens that
  // account's sheet once the list has loaded. The id leaves the URL first, so
  // the sheet's history entry sits on plain /accounts. `?account_id=new`
  // names the desktop's create form: it stays in the URL and the page shows
  // the desktop note, so widening the window still opens the form.
  useEffect(() => {
    if (!deepLink || deepLink === CREATE_MODE || loading) return;
    const params = new URLSearchParams(location.search);
    params.delete('account_id');
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true });
    const target = accounts.find((a) => String(a.id) === deepLink);
    if (target) openSheetRef.current(target);
  }, [deepLink, loading, accounts, location.pathname, location.search, navigate]);

  if (deepLink === CREATE_MODE) return <DesktopOnlyNote withHomeLink />;

  let body;
  // A refresh keeps the list on screen (and its scroll position); only the
  // first load shows the spinner.
  if (loading && !accounts.length) {
    body = <Center py="64px"><Spinner size="lg" color="phone.muted" /></Center>;
  } else if (error) {
    body = <Text textStyle="phoneBody" color="phone.expense" px="16px" py="32px">{error}</Text>;
  } else if (!accounts.length) {
    body = <Text textStyle="phoneBody" color="phone.muted" px="16px" py="32px">{t('phone.noAccounts')}</Text>;
  } else {
    body = (
      <>
        <Box px="16px" pt="8px">
          <HeaderCard
            figures={figures}
            perCurrency={perCurrencyFigures}
            approx={approx}
            currency={figureCurrency}
            unconvertible={converted ? convertedSummary : null}
            net={net}
            periods={chartPeriods}
            granularity={granularity}
            range={range}
            onRange={setRange}
          />
        </Box>
        {/* How the list is grouped, as a chip like the ones on Movements. */}
        <Flex px="16px" pt="4px">
          <Chip
            active={groupBy !== GROUP_BY_TYPE}
            onClick={() => setGroupByOpen(true)}
            trailing={<Box as={LuChevronDown} boxSize="15px" />}
          >
            {t('groupBy.chip', { value: t(`groupBy.${groupBy}`) })}
          </Chip>
        </Flex>
        {(byType ? orderedGroups(groups) : Object.keys(groups)).map((g) => (
          <AccountGroup
            key={g || 'none'}
            group={g}
            label={byType ? undefined : (g || t(EMPTY_GROUP_LABEL[groupBy]))}
            accounts={groups[g]}
            caption={groupCaption(groups[g])}
            rowProps={rowProps}
            onOpen={openSheet}
          />
        ))}
      </>
    );
  }

  return (
    <Box pb="8px">
      {body}
      <DesktopNoteCard />
      <CurrencySheet isOpen={currencyOpen} onClose={() => setCurrencyOpen(false)} />
      <GroupBySheet
        isOpen={groupByOpen}
        onClose={() => setGroupByOpen(false)}
        value={groupBy}
        onChange={(value) => { setGroupBy(value); saveGroupBy(value); }}
      />
      {accountSheet}
    </Box>
  );
}
